// =====================================================================
// src/screens/v2/MembersTabV2.jsx
//
// V2 Members Tab — list of members of a batch, grouped by city.
//
// V2 additions over V1:
//   - Business owner pill ("Owns Sharma Sweets") — tappable to open profile
//   - Filter chips (All / Admins / Owners)
//
// In Phase B, member data + business linkage will come from Firestore
// queries on the users + businesses collections.
//
// Props:
//   onBack         — return to previous screen
//   onOpenBusiness — open business profile (when biz-pill tapped)
// =====================================================================

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Search, X, MapPin, Building2, Briefcase, ChevronRight, Award
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { subscribeToGroupMembers } from '../../lib/db';
import { getBusinessesByOwners } from '../../lib/v2/firestore';
import './MembersTabV2.css';

// ============================================================
// FILTERS — built dynamically from real data
// ============================================================
//
// 'admins' chip only shows if at least one batch member is an admin.
// 'owners' chip only shows if at least one batch member owns a verified
// V2 business. Avoids empty filters cluttering the UI.
//
// Built inside the component once counts are computed.

export default function MembersTabV2({
  groupId,
  currentUser,
  onBack,
  onOpenBusiness,
  // Tap handlers wired by App.jsx — mirror V1's Members screen contract
  // so we reuse V1's MemberDetail + chat infrastructure rather than
  // building V2-specific equivalents.
  onTapMember,
  onTapSelf,
  asTab = false,
  // 6.6c lag fix: when used as a bottom-nav tab, App.jsx feeds us its
  // pre-loaded V1 member list + group name so we render instantly without
  // re-subscribing on every tab switch. Deep ?v2=members-v2 route leaves
  // these undefined → fall back to the original subscription.
  members: membersProp,
  groupName: groupNameProp,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [toast, setToast] = useState(null);

  // ============ DATA LAYER (Step 3.7) ============
  // Members from V1's /groups/{groupId}/members subcollection via the
  // same subscribeToGroupMembers used by V1's Members tab. Each member
  // arrives with V1 profile fields (name, currentCity, profession, color,
  // initials, photoUrl, isAdmin) merged in.
  const [internalMembers, setInternalMembers] = useState([]);
  // V2 enhancement: which businesses each member owns. Map<ownerUid, Business[]>.
  // Refreshed whenever the member list changes.
  const [ownedByMember, setOwnedByMember] = useState(new Map());
  // Group meta — fetched once on mount to populate the title bar.
  const [internalGroupName, setInternalGroupName] = useState('');
  // 6.6c: loading starts FALSE when members are fed via props — we already
  // have what we need to render. Only true when we'll subscribe ourselves.
  const [loading, setLoading] = useState(!membersProp);
  const [error, setError] = useState(null);

  // Resolved values — prefer props (App.jsx tab usage), fall back to
  // internal subscription state (deep ?v2=members-v2 route).
  const members = membersProp ?? internalMembers;
  const groupName = groupNameProp ?? internalGroupName;

  // === Effect 1: subscribe to live group members (V1 source of truth) ===
  // Same subscription V1's Members tab uses, so the two views stay in sync.
  // Returns an unsubscribe function; cleanup unmounts the listener cleanly.
  // 6.6c: when caller passes `members` prop (tab usage), we skip the
  // subscription entirely — App.jsx is already subscribed and feeds us.
  useEffect(() => {
    if (membersProp !== undefined) {
      // Props mode — caller owns the data.
      setLoading(false);
      return undefined;
    }
    if (!groupId) {
      setLoading(false);
      setError('No batch selected');
      return undefined;
    }
    setLoading(true);
    setError(null);
    let unsub;
    try {
      unsub = subscribeToGroupMembers(groupId, (list) => {
        setInternalMembers(Array.isArray(list) ? list : []);
        setLoading(false);
      });
    } catch (err) {
      console.error('[MembersTabV2] subscription failed:', err);
      setError(err?.message || 'Failed to load members');
      setLoading(false);
    }
    return () => { if (unsub) unsub(); };
  }, [groupId, membersProp]);

  // === Effect 2: fetch the group's name + memberCount for the title ===
  // Read-once on mount; member list changes don't refetch this.
  // 6.6c: skip when caller passes the name directly (tab usage).
  useEffect(() => {
    if (groupNameProp !== undefined) return undefined;
    if (!groupId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'groups', groupId));
        if (cancelled) return;
        if (snap.exists()) {
          const data = snap.data();
          setInternalGroupName(data.name || '');
        }
      } catch (err) {
        console.warn('[MembersTabV2] group fetch failed:', err);
        // Non-fatal — title just stays generic
      }
    })();
    return () => { cancelled = true; };
  }, [groupId, groupNameProp]);

  // === Effect 3: when members change, look up which ones own businesses ===
  // Single query (chunked at 30) — typical batch has <30 members so 1 round trip.
  const refreshOwnership = useCallback(async (memberList) => {
    if (!memberList || memberList.length === 0) {
      setOwnedByMember(new Map());
      return;
    }
    try {
      const uids = memberList.map(m => m.id).filter(Boolean);
      // includeUnverified: true so a member's just-registered (pending)
      // business shows the "Owns" pill immediately. Verified-only filtering
      // happens at display time via the ✓ badge, not here.
      const ownersMap = await getBusinessesByOwners(uids, { includeUnverified: true });
      setOwnedByMember(ownersMap);
    } catch (err) {
      console.warn('[MembersTabV2] ownership lookup failed:', err);
      // Non-fatal — pills just don't show
      setOwnedByMember(new Map());
    }
  }, []);

  useEffect(() => {
    refreshOwnership(members);
  }, [members, refreshOwnership]);

  // Auto-hide toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  // Filter + search across the live members list.
  // Field map (mock → V1 real):
  //   m.city → m.currentCity, m.role → m.profession,
  //   m.businessId/Name → derived from ownedByMember Map (multi-pill)
  const visibleMembers = useMemo(() => {
    let list = members;
    if (filter === 'admins') list = list.filter(m => m.isAdmin);
    if (filter === 'owners') list = list.filter(m => ownedByMember.has(m.id));
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(m => {
        const owned = ownedByMember.get(m.id) || [];
        return (
          (m.name || '').toLowerCase().includes(q) ||
          (m.currentCity || '').toLowerCase().includes(q) ||
          (m.profession || '').toLowerCase().includes(q) ||
          owned.some(b => (b.name || '').toLowerCase().includes(q))
        );
      });
    }
    return list;
  }, [members, ownedByMember, filter, searchQuery]);

  const grouped = useMemo(() => {
    const map = new Map();
    visibleMembers.forEach(m => {
      // V1's subscribeToGroupMembers defaults missing currentCity to '—',
      // so treat both empty AND em-dash as "No city set" for grouping.
      const rawCity = m.currentCity;
      const city = (!rawCity || rawCity === '—' || !rawCity.trim())
        ? 'No city set'
        : rawCity;
      if (!map.has(city)) map.set(city, []);
      map.get(city).push(m);
    });
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [visibleMembers]);

  const counts = useMemo(() => ({
    all: members.length,
    admins: members.filter(m => m.isAdmin).length,
    owners: members.filter(m => ownedByMember.has(m.id)).length,
  }), [members, ownedByMember]);

  // Dynamic filter chips — admins / owners only appear if non-zero
  const visibleFilters = useMemo(() => {
    const out = [{ id: 'all', label: 'All' }];
    if (counts.admins > 0) out.push({ id: 'admins', label: 'Admins' });
    if (counts.owners > 0) out.push({ id: 'owners', label: 'Business Owners' });
    return out;
  }, [counts]);

  // If the active filter just disappeared (e.g. last admin left batch),
  // snap back to 'all' so the UI doesn't show an invisible-chip selection.
  useEffect(() => {
    const stillVisible = visibleFilters.some(f => f.id === filter);
    if (!stillVisible) setFilter('all');
  }, [visibleFilters, filter]);

  function handleOpenMember(m) {
    // Self-tap → bottom-nav switch to Profile tab (V1 parity).
    // Other members → V1's MemberDetail screen (which exposes 'Open chat').
    if (currentUser && m.id === currentUser.id) {
      onTapSelf?.();
    } else if (onTapMember) {
      onTapMember(m);
    } else {
      // Defensive fallback — if parent forgot to wire the callback,
      // at least show a toast instead of silently swallowing the tap.
      setToast(`Opening ${m.name}`);
    }
  }

  function handleOpenBusinessClick(e, businessId, businessName) {
    e.stopPropagation();
    if (onOpenBusiness) onOpenBusiness(businessId);
    else setToast(`Opening ${businessName}`);
  }

  return (
    <div className={`v2-members-tab${asTab ? ' as-tab' : ''}`}>
      <div className="v2mt-top-bar">
        {!asTab && (
          <button className="v2mt-back-btn" onClick={onBack} aria-label="Back">
            <ArrowLeft size={20} />
          </button>
        )}
        <div className="v2mt-top-info">
          <div className="v2mt-top-title">{groupName || 'Batch'}</div>
          <div className="v2mt-top-sub">
            {members.length > 0
              ? `${members.length} members`
              : ''}
          </div>
        </div>
      </div>

      <div className="v2mt-search-section">
        <div className="v2mt-search-bar">
          <Search size={16} />
          <input
            type="text"
            className="v2mt-search-input"
            placeholder="Search members…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              className="v2mt-search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear"
            >
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      <div className="v2mt-filter-bar">
        {visibleFilters.map(f => (
          <button
            key={f.id}
            className={`v2mt-filter-chip ${filter === f.id ? 'active' : ''}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
            <span className="v2mt-fc-count">{counts[f.id]}</span>
          </button>
        ))}
      </div>

      <div className="v2mt-scroll-body">
        {loading ? (
          <div className="v2mt-empty">
            <div className="v2mt-empty-icon">⏳</div>
            <div className="v2mt-empty-title">Loading members…</div>
          </div>
        ) : error ? (
          <div className="v2mt-empty">
            <div className="v2mt-empty-icon">⚠️</div>
            <div className="v2mt-empty-title">Couldn't load members</div>
            <div className="v2mt-empty-sub">{error}</div>
          </div>
        ) : visibleMembers.length === 0 ? (
          <div className="v2mt-empty">
            <div className="v2mt-empty-icon">🔍</div>
            <div className="v2mt-empty-title">No matches</div>
            <div className="v2mt-empty-sub">
              Try a different name, role, or city.
            </div>
          </div>
        ) : (
          grouped.map(([city, cityMembers]) => (
            <div key={city} className="v2mt-city-group">
              <div className="v2mt-city-cap">
                <MapPin size={11} strokeWidth={2.2} />
                <span>{city.toUpperCase()} · <span className="v2mt-city-count">{cityMembers.length}</span></span>
              </div>
              <div className="v2mt-list">
                {cityMembers.map(m => {
                  const ownedBusinesses = ownedByMember.get(m.id) || [];
                  return (
                  <button
                    key={m.id}
                    className="v2mt-member-row"
                    onClick={() => handleOpenMember(m)}
                  >
                    {m.photoUrl ? (
                      <img
                        className="v2mt-avatar"
                        src={m.photoUrl}
                        alt={m.name}
                        style={{ objectFit: 'cover' }}
                      />
                    ) : (
                      <div
                        className="v2mt-avatar"
                        style={{ background: m.color || '#C45A3F' }}
                      >
                        {m.initials || (m.name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="v2mt-body">
                      <div className="v2mt-name-line">
                        <span className="v2mt-name">{m.name}</span>
                        {m.isAdmin && (
                          <span className="v2mt-admin-badge">
                            <Award size={9} strokeWidth={2.5} />
                            ADMIN
                          </span>
                        )}
                      </div>
                      <div className="v2mt-pills-row">
                        {ownedBusinesses.map(biz => (
                          <span
                            key={biz.id}
                            className="v2mt-biz-pill"
                            onClick={e => handleOpenBusinessClick(e, biz.id, biz.name)}
                          >
                            <Building2 size={10} />
                            Owns {biz.name}
                          </span>
                        ))}
                        {m.profession && (
                          <span className="v2mt-role-pill">
                            <Briefcase size={10} />
                            {m.profession}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight size={16} className="v2mt-chev" />
                  </button>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {toast && <div className="v2mt-toast">{toast}</div>}
    </div>
  );
}
